<!-- eslint-disable vue/prop-name-casing -->
<script setup>
import { Tippy } from 'vue-tippy';
import { useCommonImports } from '~/common/composables/common-imports.composable.js';
import { get_transmittal_status } from '~/dms/constants';
import { useTransmittalsStore } from '~/dms/store/transmittals.store.js';

const props = defineProps({
  store_key: {
    type: String,
  },
  transmittals: {
    type: Array,
    default: () => ([]),
  },
  is_loading: {
    type: Boolean,
    default: false,
  },
  pagination_config: {
    type: Object,
  },
  search: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['loadMore', 'tableInstanceCreated', 'rowSelect']);
const { $t } = useCommonImports();
const router = useRouter();
const route = useRoute();
const active_tab = inject('active_transmittal_tab');
const transmittals_store = useTransmittalsStore(props?.store_key);
const { TRANSMITTAL_STATUS } = get_transmittal_status();
const custom_classes = 'group-hover:!bg-gray-50';
const table_columns = [
  // route check dms/transmittal as this component is used in dashboards widget as well
  ...(route.name === 'files-transmittals'
    ? [{
        id: 'select',
        header: '',
        accessorKey: 'select',
        static: true,

      }]
    : []),
  {
    header: $t('Name'),
    accessorKey: 'name',
    id: 'name',
    custom_classes,
  },
  {
    header: $t('Status'),
    accessorKey: 'status',
    id: 'status',
    custom_classes,
  },
  {
    header: $t('Created on'),
    accessorKey: 'created_at',
    id: 'created_at',
    custom_classes,
  },
  {
    header: $t('Issue purpose'),
    accessorKey: 'issue_purpose',
    id: 'issue_purpose',
    custom_classes,
  },
  {
    header: $t('Category'),
    accessorKey: 'category',
    id: 'category',
    custom_classes,
  },
  {
    header: $t('Progress'),
    accessorKey: 'total',
    id: 'total',
    size: 200,
    custom_classes,
  },
  {
    header: $t('Updated on'),
    accessorKey: 'latest_message_timestamp',
    id: 'latest_message_timestamp',
    custom_classes,
  },
];

const transmittals = computed(() => {
  if (props.transmittals?.length) {
    return props.transmittals.map((transmittal) => {
      const status = getStatus(transmittal);
      return {
        ...transmittal,
        status_label: status?.label,
        status_color: status?.color,
      };
    });
  }

  return [];
});

function getStatus(transmittal) {
  return transmittals_store.transmittal_status(transmittal);
}

function getDocuments(documents) {
  return Array.isArray(documents) ? documents : Object.values(documents || {});
}

function document_status_color(document) {
  if (['pending_review'].includes(document?.status))
    return 'yellow';
  return TRANSMITTAL_STATUS[document?.draft?.status || document.status]?.color;
}

function document_status_name(document, document_status_map) {
  if (['pending_review'].includes(document?.status))
    return document.user_role === 'submitter' ? $t('Waiting for review') : $t('Pending review');
  if (document?.status === 'not_submitted')
    return document.user_role === 'submitter' ? $t('Pending submission') : $t('Waiting for submission');
  if (['waiting_on_other_transmittal'].includes(document?.status))
    return $t('Waiting on other transmittal');
  return document_status_map?.[document?.status] || TRANSMITTAL_STATUS[document.status]?.title || document?.status;
}

function rowClicked(transmittal) {
  const { uid, published, cancelled } = transmittal;
  const search = props.search?.toLowerCase();

  // True only when the search keyword doesn't match the transmittal's own fields,
  // meaning it must be targeting document-level content.
  const is_document_search_keyword = search
    && !transmittal.number?.toLowerCase().includes(search)
    && !transmittal.category_name?.toLowerCase().includes(search)
    && !transmittal.description?.toLowerCase().includes(search);

  const search_query = is_document_search_keyword ? { search: props.search } : {};

  if (!published && active_tab.value === 'unpublished' && !cancelled) {
    router.push({ name: 'files-create-transmittals', query: { transmittal: uid, ...search_query } });
  }
  else {
    const query = {
      ...(transmittal.status === 'Review Completed' ? { submitted_by_me: true } : {}),
      ...search_query,
    };
    router.push({
      name: 'files-transmittals-detail',
      params: { asset_id: route.params.asset_id, transmittal_uid: uid },
      query: Object.keys(query).length ? query : undefined,
    });
  }
}

function formatTable(cell) {
  if (cell.row.original?.unread) {
    return {
      backgroundColor: 'white',
    };
  }
  else {
    return {
      backgroundColor: '#FCFCFD',
    };
  }
}

function onRowSelect(items) {
  emit('rowSelect', items);
}

defineExpose({
  columns: table_columns,
});
</script>

<template>
  <div>
    <div id="transmittal-list" class="my-4">
      <HawkTable
        :pagination_config="pagination_config"
        is_gapless
        :data="transmittals"
        :columns="table_columns"
        :sticky_group_label="true"
        :manual_pagination="true"
        :format-table="formatTable"
        :is_loading="is_loading"
        @pagination="emit('loadMore', $event)"
        @row-clicked="rowClicked"
        @select-row="onRowSelect"
        @table-instance="emit('tableInstanceCreated', $event)"
      >
        <template #name="{ data: { row: { original } } }">
          <div v-if="original?.confidential" v-tippy="$t('Confidential')" class="flex items-center -ml-6 mr-1 justify-evenly gap-2 text-gray-600">
            <IconHawkLockOne class="w-5 h-5" />
          </div>
          <div class="text-gray-900  text-sm">
            <div class="text-xs text-gray-600 font-medium">
              {{ original.number }}
            </div>
            <div :class="original.unread ? 'font-semibold' : ''">
              {{ original.name }}
            </div>
          </div>
        </template>
        <template #status="{ data: { row: { original: { status, status_color, status_label } } } }">
          <hawk-badge
            v-if="status"
            :color="status_color"
          >
            {{ status_label }}
          </hawk-badge>
          <span v-else>-</span>
        </template>
        <template #category="{ data: { row: { original: { category } } } }">
          <div class="text-gray-700 font-medium text-xs">
            <hawk-category-name :uid="category" />
          </div>
        </template>
        <template #priority="{ data: { row: { original: { priority } } } }">
          <TaskPriority :priority="transmittals_store.priority_value(priority)" />
        </template>
        <template #created_at="{ data: { row: { original: { created_at } } } }">
          <div class="text-gray-600 text-sm">
            {{ $date(created_at, 'DATE_MED') }}
          </div>
        </template>
        <template #latest_message_timestamp="{ data: { row: { original } } }">
          <div class=" text-sm" :class="original.unread ? 'text-gray-900 font-medium' : 'text-gray-600'">
            {{ original.latest_message_timestamp ? $date(original.latest_message_timestamp, 'DATE_MED') : "" }}
          </div>
        </template>
        <template #total="{ data: { row: { original: { total, completed, documents, document_status_map } } } }">
          <Tippy
            v-if="getDocuments(documents).length"
            theme="documents-popover"
            placement="top"
            :interactive="true"
            :max-width="420"
          >
            <hawk-progress
              class="flex-auto min-w-48"
              :progress="total ? (completed / total) * 100 : 0"
            >
              <div>
                {{ completed }}/{{ total }}
              </div>
            </hawk-progress>
            <template #content>
              <div class="max-h-[280px] w-[320px] overflow-auto scrollbar text-left">
                <div
                  v-for="document in getDocuments(documents)"
                  :key="document.uid"
                  class="px-3 py-2"
                >
                  <div class="flex items-center justify-between gap-2">
                    <div class="flex-1 min-w-0">
                      <div v-if="document.number" class="text-xs text-gray-600 font-medium">
                        {{ document.number }}
                      </div>
                      <div class="text-sm text-gray-900 font-semibold truncate ">
                        {{ document.name }}
                      </div>
                    </div>

                    <div class="flex-none shrink-0 flex justify-end items-end">
                      <hawk-badge :color="document_status_color(document)">
                        {{ document_status_name(document, document_status_map) }}
                      </hawk-badge>
                    </div>
                  </div>
                </div>
              </div>
            </template>
          </Tippy>
          <hawk-progress
            v-else
            class="flex-auto"
            :progress="total ? (completed / total) * 100 : 0"
          >
            <div>
              {{ completed }}/{{ total }}
            </div>
          </hawk-progress>
        </template>
      </HawkTable>
    </div>
  </div>
</template>

<style>
.tippy-box[data-theme~='documents-popover'] {
  background-color: #fff;
  color: #101828;
  border: 1px solid #eaecf0;
  border-radius: 8px;
  box-shadow: 0 4px 6px -2px rgba(16, 24, 40, 0.03), 0 12px 16px -4px rgba(16, 24, 40, 0.08);
}

.tippy-box[data-theme~='documents-popover'] .tippy-arrow {
  border-bottom-color: white;
  display:none;
}
</style>
